// Polyfills
import 'es6-object-assign/auto';
import 'core-js/fn/array/includes';
import 'core-js/fn/promise/finally';
import 'intl'; // For Safari 9

import React from 'react';
import ReactDomClient from 'react-dom/client';

import AppStateHOC from '../lib/app-state-hoc.jsx';
import BrowserModalComponent from '../components/browser-modal/browser-modal.jsx';
import supportedBrowser from '../lib/supported-browser';

import {auth} from '../auth/firebase.js';
import {
    signInWithCustomToken,
    onAuthStateChanged
} from 'firebase/auth';

import {
    getFirestore,
    doc,
    getDoc
} from 'firebase/firestore';

import LoggedOutScreen from '../components/loggedOutScreen.jsx';

import styles from './index.css';

// ---------------------------------------
// APP TARGET
// ---------------------------------------

const appTarget = document.createElement('div');
appTarget.className = styles.app;
document.body.appendChild(appTarget);

// ---------------------------------------
// VIEWER MODE
// ---------------------------------------

const getViewerMode = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('view') === 'viewer';
};

const isViewer = getViewerMode();
const openedFromAlydMarket = Boolean(window.opener);

let editorRendered = false;

const ALLOWED_PARENT_ORIGINS = [
    'https://alydmarket.com',
    'https://securenfts.alydmarket.com'
];

if (isViewer) {
    sessionStorage.setItem(
        'viewerNeedsFullscreen',
        'true'
    );

    window.onbeforeunload = null;
}

// ---------------------------------------
// SINGLE VIEWER LOADING OVERLAY
// ---------------------------------------

const showViewerLoadingOverlay = () => {
    const existing =
        document.getElementById('firebase-loading-overlay');

    if (existing) {
        return existing;
    }

    const overlay = document.createElement('div');

    /*
     * IMPORTANT:
     * firebaseLoader.jsx uses this exact same ID.
     *
     * That means index.jsx creates the overlay at startup and
     * firebaseLoader reuses it instead of making a second screen.
     */
    overlay.id = 'firebase-loading-overlay';
    overlay.textContent = 'Loading your project...';

    Object.assign(overlay.style, {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#fff',
        zIndex: 2147483647,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '1.5rem'
    });

    document.body.appendChild(overlay);

    console.log(
        '[INDEX] Viewer loading overlay created'
    );

    return overlay;
};

const hideViewerLoadingOverlay = () => {
    const overlay =
        document.getElementById('firebase-loading-overlay');

    if (overlay) {
        overlay.remove();

        console.log(
            '[INDEX] Viewer loading overlay removed'
        );
    }
};

/*
 * Viewer mode gets its loading screen immediately.
 *
 * This happens BEFORE Scratch GUI is rendered so the user never needs
 * to see the editor initializing underneath.
 */
if ((isViewer || openedFromAlydMarket) && !editorRendered) {
    showViewerLoadingOverlay();
}

// ---------------------------------------
// SUPPORTED BROWSER
// ---------------------------------------

if (supportedBrowser()) {
    let loggedOutRoot = null;

    const authGraceMs = 2000;

    const renderLoggedOut = () => {
        if (editorRendered || loggedOutRoot) {
            return;
        }

        console.log(
            '[INDEX] Rendering LoggedOutScreen'
        );

        /*
         * Do not leave the loading overlay covering an actual
         * logged-out state.
         */
        hideViewerLoadingOverlay();

        loggedOutRoot =
            ReactDomClient.createRoot(appTarget);

        loggedOutRoot.render(
            <LoggedOutScreen />
        );
    };

    const renderEditor = () => {
        if (editorRendered) {
            return;
        }

        /*
         * If LoggedOutScreen was rendered because authentication
         * took longer than the grace period, remove it first.
         */
        if (loggedOutRoot) {
            loggedOutRoot.unmount();
            loggedOutRoot = null;

            /*
             * Authentication eventually succeeded.
             * Put the viewer cover back before Scratch mounts.
             */
           if (isViewer || openedFromAlydMarket) {
    showViewerLoadingOverlay();
}
        }

        editorRendered = true;

        console.log(
            '[INDEX] Rendering Scratch GUI'
        );

        console.log(
            '[EDITOR] viewerMode =',
            isViewer
        );

        /*
         * Modern Scratch render path.
         *
         * In viewer mode the loading overlay intentionally remains
         * ABOVE Scratch after this call.
         *
         * firebaseLoader.jsx will remove it only after:
         *
         * 1. Firebase project download
         * 2. SB3 injection
         * 3. vm.loadProject()
         * 4. Scratch rendering
         * 5. fullscreen activation
         *
         * DO NOT remove the overlay here in viewer mode.
         */
        require('./render-gui.jsx').default(
            appTarget,
            isViewer
        );
    };

    // ---------------------------------------
    // AUTH FALLBACK
    // ---------------------------------------

    const authFallbackTimer =
        setTimeout(() => {
            if (
                !auth.currentUser &&
                !editorRendered
            ) {
                renderLoggedOut();
            }
        }, authGraceMs);

    // ---------------------------------------
    // FIREBASE AUTH LISTENER
    // ---------------------------------------

    onAuthStateChanged(auth, user => {
        console.log(
            '[AUTH] Firebase user changed:',
            user ? user.uid : null
        );

  if (user) {
    clearTimeout(authFallbackTimer);

    if (isViewer || openedFromAlydMarket) {
        showViewerLoadingOverlay();
    }

    renderEditor();

    /*
     * Viewer keeps the overlay until the SB3/project has loaded.
     * Normal Editor only needs it through authentication + initial render.
     */
    if (!isViewer && openedFromAlydMarket) {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                setTimeout(() => {
                    hideViewerLoadingOverlay();
                }, 300);
            });
        });
    }
}
    });

    // ---------------------------------------
    // ALYD POSTMESSAGE BRIDGE
    // ---------------------------------------

    window.addEventListener(
        'message',
        async event => {
                     if (
                !ALLOWED_PARENT_ORIGINS.includes(event.origin)
            ) {
                return;
            }

           const {
    type,
    path,
    projectId,
    firebaseToken,
    therapy
} = event.data || {};

            /*
             * Store the selected Firebase Storage project path.
             */

            /*
 * Therapy project launch from Alyd Market.
 *
 * Alyd only sends the Firestore project ID.
 * Scratch retrieves the saved project metadata itself.
 */
if (
    type === 'therapy-project' &&
    projectId
) {
    console.log(
        '[EDITOR] Received Therapy project ID:',
        projectId
    );

    try {
        /*
         * The Firebase token message and Therapy project message
         * are sent separately, so make sure authentication has
         * completed before reading the user's project document.
         */
        let currentUser = auth.currentUser;

        if (!currentUser) {
            currentUser = await new Promise((resolve, reject) => {
                const timeout = setTimeout(() => {
                    unsubscribe();

                    reject(
                        new Error(
                            'Timed out waiting for Firebase authentication'
                        )
                    );
                }, 10000);

                const unsubscribe = onAuthStateChanged(
                    auth,
                    user => {
                        if (!user) {
                            return;
                        }

                        clearTimeout(timeout);
                        unsubscribe();
                        resolve(user);
                    }
                );
            });
        }

        console.log(
            '[EDITOR] Loading Therapy project for user:',
            currentUser.uid
        );

        const firestore = getFirestore();

        const projectRef = doc(
            firestore,
            'therapyProjects',
            currentUser.uid,
            'projects',
            projectId
        );

        const projectSnapshot =
            await getDoc(projectRef);

        if (!projectSnapshot.exists()) {
            throw new Error(
                `Therapy project ${projectId} was not found`
            );
        }

        const project =
            projectSnapshot.data();

        if (!project.storagePath) {
            throw new Error(
                'Therapy project is missing its storagePath'
            );
        }

        console.log(
            '[EDITOR] Therapy project found:',
            {
                projectId,
                storagePath: project.storagePath,
                hasTherapyState: Boolean(project.therapy)
            }
        );

        /*
         * firebaseLoader.jsx already watches this value.
         * Setting it here causes the existing SB3 loading
         * pipeline to take over.
         */
        sessionStorage.setItem(
            'firebaseProjectPath',
            project.storagePath
        );

        /*
         * Keep the Therapy state available until gui.jsx
         * restores it after the SB3 has loaded.
         */
        if (project.therapy) {
            sessionStorage.setItem(
                'therapyProjectState',
                JSON.stringify(project.therapy)
            );
        } else {
            sessionStorage.removeItem(
                'therapyProjectState'
            );
        }
    } catch (error) {
        console.error(
            '[EDITOR] Failed to load Therapy project metadata:',
            error
        );

        hideViewerLoadingOverlay();
    }
}
            if (
    type === 'firebase-storage-path' &&
    path
) {
    const decodedPath =
        decodeURIComponent(path);

    console.log(
        '[EDITOR] Received Firebase project path:',
        decodedPath
    );

    console.log(
        '[EDITOR] Purchased project Therapy data:',
        therapy || null
    );

    /*
     * Keep the viewer covered while the new project
     * path is being handed to firebaseLoader.
     */
    if (isViewer) {
        showViewerLoadingOverlay();
    }

    sessionStorage.setItem(
        'firebaseProjectPath',
        decodedPath
    );

if (therapy) {
    sessionStorage.setItem(
        'therapyProjectState',
        JSON.stringify(therapy)
    );
}
}

            /*
             * Authenticate only when the message actually contains
             * a Firebase custom token.
             */
            if (firebaseToken) {
                console.log(
                    '[EDITOR] Received Firebase token via postMessage'
                );

                localStorage.setItem(
                    'firebaseToken',
                    firebaseToken
                );

                try {
                    const userCred =
                        await signInWithCustomToken(
                            auth,
                            firebaseToken
                        );

                    console.log(
                        '[EDITOR] Firebase login success:',
                        userCred.user.email ||
                        userCred.user.uid
                    );

                    localStorage.removeItem(
                        'firebaseToken'
                    );
                } catch (error) {
                    console.error(
                        '[EDITOR] Firebase login failed:',
                        error
                    );

                    localStorage.removeItem(
                        'firebaseToken'
                    );

                    /*
                     * Authentication failed, so don't leave the
                     * viewer permanently covered.
                     */
                    hideViewerLoadingOverlay();
                }
            }
        }
    );

    // ---------------------------------------
    // LOCAL TOKEN RESTORE
    // ---------------------------------------

    const localToken =
        localStorage.getItem('firebaseToken');

    if (localToken) {
        console.log(
            '[EDITOR] Logging in via stored Firebase token...'
        );

        signInWithCustomToken(
            auth,
            localToken
        )
            .then(userCred => {
                console.log(
                    '[EDITOR] Firebase login success:',
                    userCred.user.email ||
                    userCred.user.uid
                );

                localStorage.removeItem(
                    'firebaseToken'
                );
            })
            .catch(error => {
                console.error(
                    '[EDITOR] Firebase login failed:',
                    error
                );

                localStorage.removeItem(
                    'firebaseToken'
                );

                hideViewerLoadingOverlay();
            });
    }

    // ---------------------------------------
    // POPUP COMMUNICATION
    // ---------------------------------------

    if (window.opener) {
        /*
         * The viewer overlay has already been created above.
         *
         * Do NOT create another loading screen here.
         */
               ALLOWED_PARENT_ORIGINS.forEach(origin => {
            window.opener.postMessage('editor-ready', origin);
        });

        console.log(
            '[EDITOR] Sent "editor-ready"'
        );
    }

} else {
    // ---------------------------------------
    // UNSUPPORTED BROWSER
    // ---------------------------------------

    hideViewerLoadingOverlay();

    BrowserModalComponent.setAppElement(
        appTarget
    );

    const WrappedBrowserModalComponent =
        AppStateHOC(
            BrowserModalComponent,
            true /* localesOnly */
        );

    const handleBack = () => {};

    const root =
        ReactDomClient.createRoot(
            appTarget
        );

    // eslint-disable-next-line react/jsx-no-bind
    root.render(
        <WrappedBrowserModalComponent
            onBack={handleBack}
        />
    );
}